import { useState } from 'react';
import { Feature } from '#/types/message';
// import ContentCarousel from '../messages/content-carousel/content-carousel';
import deleteIcon from '#/assets/delete.svg';
import editIcon from '#/assets/edit.svg';
import { useTranslation } from 'react-i18next';
import './feature-selection.css';
// import { ContentModal } from '#/components/content-modal/content-modal';

interface FeatureSelectionProps {
  features: Feature[];
  onClose: () => void;
  onFeatureSelect: (selections: { feature_name: string, option_name: string }[]) => void;
}

export function FeatureSelection({
  features,
  onClose,
  onFeatureSelect
}: FeatureSelectionProps) {
  const { t } = useTranslation();
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [selections, setSelections] = useState<Map<string, string>>(new Map());
  const [showConfirmation, setShowConfirmation] = useState(false);
  // const [isModalOpen, setModalOpen] = useState(false);
  // const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
  // const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  // const [currentImageUrls, setCurrentImageUrls] = useState<string[]>([]);

  const currentFeature = features[currentFeatureIndex];
  const isLastFeature = currentFeatureIndex === features.length - 1;
  const isSingleFeatureEdit = features.length === 1;

  const handleOptionSelect = (featureName: string, optionName: string) => {
    setSelections(new Map(selections.set(featureName, optionName)));
  };

  const handlePrevious = () => {
    if (currentFeatureIndex > 0) {
      setCurrentFeatureIndex(prev => prev - 1);
      setShowConfirmation(false);
    }
  };

  const handleConfirm = () => {
    const selectionsArray = features.map(feature => {
      if (feature.is_addon && feature.options.length === 1) {
        const selection = selections.get(feature.feature_name);
        return {
          feature_name: feature.feature_name,
          option_name: selection === 'no' ? 'N/A' : feature.options[0].option_name
        };
      }
      const optionName = selections.get(feature.feature_name);
      return optionName ? {
        feature_name: feature.feature_name,
        option_name: optionName
      } : null;
    }).filter((item): item is { feature_name: string; option_name: string } => item !== null);

    onFeatureSelect(selectionsArray);
  };

  const handleContinue = () => {
    if (isSingleFeatureEdit) {
      handleConfirm();
    } else if (!isLastFeature) {
      setCurrentFeatureIndex(prev => prev + 1);
    } else {
      setShowConfirmation(true);
    }
  };

  const getProgressDotClass = (index: number) => {
    if (showConfirmation) {
      // When in confirmation view, all dots should be completed
      return 'progress-dot completed';
    }
    if (index < currentFeatureIndex) {
      // Previous features are completed
      return 'progress-dot completed';
    }
    if (index === currentFeatureIndex) {
      // Current feature is active
      return 'progress-dot active';
    }
    // Future features are neither active nor completed
    return 'progress-dot';
  };

  // const handleImageClick = (url: string, index: number, urls: string[]) => {
  //   setModalImageUrl(url);
  //   setCurrentImageUrls(urls);
  //   setActiveImageIndex(index);
  //   setModalOpen(true);
  // };

  // const handleNextImage = () => {
  //   if (activeImageIndex < currentImageUrls.length - 1) {
  //     const nextIndex = activeImageIndex + 1;
  //     setActiveImageIndex(nextIndex);
  //     setModalImageUrl(currentImageUrls[nextIndex]);
  //   }
  // };

  // const handlePreviousImage = () => {
  //   if (activeImageIndex > 0) {
  //     const prevIndex = activeImageIndex - 1;
  //     setActiveImageIndex(prevIndex);
  //     setModalImageUrl(currentImageUrls[prevIndex]);
  //   }
  // };

  return (
    <div className="feature-selection-panel">
      <div className="feature-header">
        <button className="feature-selection-close-button" onClick={onClose}>
          <img src={deleteIcon} alt="close" />
        </button>
        <div className="feature-header-text">
          <p>{t('selectFeature')}:</p>
          <p className="feature-name-bold">{currentFeature.name}</p>
        </div>
      </div>

      {!showConfirmation ? (
        <>
          <div className="options-container">
            {currentFeature.is_addon && currentFeature.options.length === 1 ? (
              <div className="addon-option-card">
                <h3>{currentFeature.name}</h3>
                <p>{t('accessorySupport')}</p>
                <div className="options-container">
                  <div
                    className={`option-card ${selections.get(currentFeature.feature_name) === currentFeature.options[0].option_name ? 'selected' : ''}`}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      const isImageClick = target.tagName.toLowerCase() === 'img' ||
                        target.closest('.image-container') !== null;

                      if (!isImageClick) {
                        setSelections(new Map(selections.set(currentFeature.feature_name, currentFeature.options[0].option_name)));
                      }
                    }}
                  >
                    <p className="feature-name-bold">{t('yes')}</p>
                  </div>
                  <div
                    className={`option-card ${selections.get(currentFeature.feature_name) === 'no' ? 'selected' : ''}`}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      const isImageClick = target.tagName.toLowerCase() === 'img' ||
                        target.closest('.image-container') !== null;

                      if (!isImageClick) {
                        setSelections(new Map(selections.set(currentFeature.feature_name, 'no')));
                      }
                    }}
                  >
                    <p className="feature-name-bold">{t('no')}</p>
                  </div>
                </div>
              </div>
            ) : (
              currentFeature.options.map((option) => (
                <div
                  key={option.option_name}
                  className={`option-card ${selections.get(currentFeature.feature_name) === option.option_name ? 'selected' : ''}`}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    const isImageClick = target.tagName.toLowerCase() === 'img' ||
                      target.closest('.image-container') !== null;

                    if (!isImageClick) {
                      setSelections(new Map(selections.set(currentFeature.feature_name, option.option_name)));
                    }
                  }}
                >
                  <div className="image-container" onClick={(e) => {
                    e.stopPropagation();
                    handleOptionSelect(currentFeature.feature_name, option.option_name);
                  }}
                  />
                  {/* TODO: Fix this */}
                  {/* {option.images.length > 0 && (
                    <ContentCarousel
                      items={option.images}
                      onItemClick={() => {
                        handleImageClick(url, index, urls)
                      }}
                    />
                  )} */}
                  <div className="option-name">{option.name}</div>
                </div>
              ))
            )}
          </div>

          <div className="navigation-controls">
            {currentFeatureIndex > 0 && (
              <button
                className="nav-button previous"
                onClick={handlePrevious}
              >
                {t('previous')}
              </button>
            )}
            <button
              className="nav-button continue"
              onClick={handleContinue}
              disabled={!selections.has(currentFeature.feature_name)}
            >
              {isSingleFeatureEdit ? t('save') : isLastFeature ? t('review') : t('continue')}
            </button>
          </div>
        </>
      ) : (
        <div className="confirmation-container">
          <h4>{t('confirmSelections')}</h4>
          {features.map((feature) => {
            const optionName = selections.get(feature.feature_name);
            const option = feature.options.find(o => o.option_name === optionName);

            return (
              <div key={feature.feature_name} className="selection-summary">
                <div className="selection-details">
                  <div className="feature-name">{feature.name}</div>
                  <span className="feature-value">
                    {feature.is_addon && feature.options.length === 1
                      ? (optionName === 'no' ? t('no') : t('yes'))
                      : option?.name}
                  </span>
                </div>
                <button
                  className="feature-edit-confirmation-button"
                  onClick={() => {
                    setCurrentFeatureIndex(features.findIndex(f => f.feature_name === feature.feature_name));
                    setShowConfirmation(false);
                  }}
                >
                  <img src={editIcon} alt="edit" className="edit-icon-confirmation" />
                </button>
              </div>
            );
          })}
          <div className="confirmation-buttons">
            <button
              className="secondary-button"
              onClick={() => setShowConfirmation(false)}
            >
              {t('back')}
            </button>
            <button
              className="primary-button"
              onClick={handleConfirm}
            >
              {t('save')}
            </button>
          </div>
        </div>
      )}

      {/* Hide progress dots for single feature edit */}
      {!isSingleFeatureEdit && (
        <div className="feature-progress">
          {features.map((_, index) => (
            <div
              key={index}
              className={getProgressDotClass(index)}
            />
          ))}
        </div>
      )}

      {/* <ContentModal
        isOpen={isModalOpen}
        currentItem={modalImageUrl}
        items={currentImageUrls}
        activeIndex={activeImageIndex}
        onClose={() => setModalOpen(false)}
        onNext={handleNextImage}
        onPrevious={handlePreviousImage}
      /> */}
    </div>
  );
} 